/*
 * LA DEUDA DESCOMPUESTA, VISTA DESDE LA PANTALLA — incremento 18/21.
 *
 * QUÉ HACE Y QUÉ NO HACE ESTE ARCHIVO, porque la frontera importa:
 *
 *   SÍ · Deriva lo que la pantalla tiene que ENSEÑAR MIENTRAS SE ESCRIBE, antes de
 *        guardar: cuánto quedaría en cobro y si la cifra cabe en la deuda. Es la
 *        vista previa que el prototipo v39 dibuja en el alta y en la corrección, y
 *        sin ella la persona descubriría el rechazo al guardar.
 *
 *   NO · No decide. LA VALIDACIÓN ES DEL SERVIDOR (RN-688, D-390): aquí no se
 *        aplica ninguna cota que el servidor no aplique, y el rechazo real llega
 *        de él con su código. Si esta vista previa y el servidor discreparan,
 *        manda el servidor.
 *
 * LA COTA ES CONTRA LA DEUDA TOTAL, NO CONTRA LA DEUDA INICIAL (RN-688, D-390).
 * Es la precisión que más fácil se pierde al leer la pantalla en vez del análisis:
 * la deuda total es la deuda inicial MÁS el efecto de los movimientos no
 * cancelados —la cifra que el servidor ya manda en `deuda`— y si esta edición
 * también corrige la deuda inicial, la cota se evalúa contra el RESULTADO
 * (RN-689). En el ALTA las dos cifras coinciden, porque la cuenta nace sin
 * movimientos (RF-248): no es otra regla, es la misma.
 *
 * Los importes viajan como CADENA DECIMAL (RNF-001) y aquí se leen con punto
 * decimal; ninguna función de este archivo formatea: el formato es del núcleo.
 */

/** Lee una cadena decimal. Devuelve NaN si no es un importe legible. */
export function aNumero(valor) {
  if (valor === null || valor === undefined) return 0;
  const limpio = String(valor).replace(/,/g, '').trim();
  if (limpio === '') return 0;
  if (!/^-?\d+(\.\d{1,2})?$/.test(limpio)) return Number.NaN;
  return Number.parseFloat(limpio);
}

/** Dos decimales, para comparar sin arrastrar flotantes. */
function aDosDecimales(numero) {
  return Math.round(numero * 100) / 100;
}

/**
 * DEUDA TOTAL QUE QUEDARÍA tras guardar esta pantalla (RN-688, RN-689).
 *
 *   · En el ALTA (`cuenta` ausente): es la deuda inicial que se está capturando.
 *   · En la EDICIÓN: la deuda total de hoy, ajustada por el cambio de la deuda
 *     inicial que esta misma operación hace. El efecto de los movimientos no
 *     cancelados es el mismo antes y después, porque aquí no se toca ningún
 *     movimiento.
 *
 * Devuelve `null` cuando no se puede saber —la respuesta vino sin saldos—: sin la
 * cifra NO SE INVENTA una cota, y la pantalla deja que responda el servidor.
 */
export function deudaTotalResultante(cuenta, saldoInicialEscrito) {
  const nuevo = aNumero(saldoInicialEscrito);
  if (Number.isNaN(nuevo)) return null;

  if (!cuenta) return aDosDecimales(nuevo);

  if (cuenta.deuda === null || cuenta.deuda === undefined) return null;

  const deudaDeHoy = aNumero(cuenta.deuda);
  const inicialDeHoy = aNumero(cuenta.saldoInicial);
  if (Number.isNaN(deudaDeHoy) || Number.isNaN(inicialDeHoy)) return null;

  return aDosDecimales(deudaDeHoy - inicialDeHoy + nuevo);
}

/**
 * VISTA PREVIA DEL REPARTO (RF-248, RF-249, RN-687). Tres cifras y un veredicto:
 *
 *   · `deudaInicial`  lo que se está capturando o corrigiendo.
 *   · `diferido`      lo que se declara como compras a meses.
 *   · `enCobro`       DERIVADO: deuda inicial menos diferido (RN-687). No se
 *                     guarda en ninguna parte y no existe columna para él.
 *   · `cabe`          si la cifra satisface la cota de RN-688 contra la deuda
 *                     total resultante. `null` cuando la deuda total no se pudo
 *                     saber: entonces la pantalla no afirma ni niega nada.
 *
 * `hayImporteIlegible` separa «escribió algo que no es un importe» de «escribió
 * una cifra que no cabe»: son dos avisos distintos y el prototipo los distingue.
 */
export function repartoDeLaDeuda({ cuenta, saldoInicialEscrito, diferidoEscrito }) {
  const deudaInicial = aNumero(saldoInicialEscrito);
  const diferido = aNumero(diferidoEscrito);
  const hayImporteIlegible = Number.isNaN(deudaInicial) || Number.isNaN(diferido);

  const deudaTotal = hayImporteIlegible ? null : deudaTotalResultante(cuenta, saldoInicialEscrito);

  let cabe = null;
  if (!hayImporteIlegible && deudaTotal !== null) {
    // LOS DOS BORDES SON VÁLIDOS (CA-18-17, CA-18-18): 0 se acepta y la deuda
    // total EXACTA se acepta. Un centavo más, no.
    cabe = aDosDecimales(diferido) >= 0 && aDosDecimales(diferido) <= deudaTotal;
  }

  return {
    hayImporteIlegible,
    deudaInicial: hayImporteIlegible ? null : aDosDecimales(deudaInicial),
    diferido: hayImporteIlegible ? null : aDosDecimales(diferido),
    // Derivado, nunca guardado (RN-687).
    enCobro: hayImporteIlegible ? null : aDosDecimales(deudaInicial - diferido),
    deudaTotal,
    cabe,
    hayDiferido: !hayImporteIlegible && aDosDecimales(diferido) > 0,
  };
}

/**
 * EL DIFERIDO YA GUARDADO QUEDÓ FUERA DE RANGO porque la deuda bajó después
 * (RN-706, D-391). No es un error, no bloquea nada y NADA se ajusta solo: la
 * pantalla solo lo dice y pide corregirlo.
 *
 * Se evalúa contra lo GUARDADO, no contra lo que se está escribiendo: mientras la
 * persona teclea, el aviso que corresponde es el de la cota (RN-688).
 */
export function diferidoGuardadoFueraDeRango(cuenta) {
  if (!cuenta?.esCredito) return false;
  if (cuenta.deuda === null || cuenta.deuda === undefined) return false;

  const diferido = aNumero(cuenta.diferidoDeclarado);
  const deuda = aNumero(cuenta.deuda);
  if (Number.isNaN(diferido) || Number.isNaN(deuda)) return false;

  return aDosDecimales(diferido) > 0 && aDosDecimales(diferido) > aDosDecimales(deuda);
}

/**
 * Porcentaje de la deuda inicial que queda EN COBRO, para la barra de dos tramos
 * del detalle de la tarjeta. Presentación pura: ninguna decisión depende de esto.
 */
export function porcentajeEnCobro(deudaInicial, diferido) {
  const total = aNumero(deudaInicial);
  const parte = aNumero(diferido);
  if (Number.isNaN(total) || Number.isNaN(parte) || total <= 0) return 0;
  return Math.max(0, Math.min(100, Math.round(((total - parte) / total) * 100)));
}
